import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, SealCheck, Package, Handshake } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const PERKS = [
  { icon: Package, title: "Wholesale Pricing Tiers", body: "Volume-based pricing from 5,000 units, with white-glove logistics to 40+ countries." },
  { icon: SealCheck, title: "Full Certification Dossier", body: "GMP, organic certificates, per-batch lab reports and MSDS — export-ready documentation." },
  { icon: Handshake, title: "Dedicated Partner Desk", body: "A named account manager, launch marketing assets and regional exclusivity options." },
];

const INITIAL = { full_name: "", company: "", email: "", country: "", channel: "", monthly_volume: "", message: "", wants_certification: false };

const inputClass = "bg-[#F5F1E8]/5 border-[#F5F1E8]/20 text-[#F5F1E8] placeholder:text-[#F5F1E8]/35 focus-visible:ring-[#C5A880] h-11 rounded-lg";

const PartnerPortal = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.company || !form.email || !form.country || !form.channel || !form.monthly_volume) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner-inquiries`, { ...form, message: form.message || null });
      toast.success("Inquiry received. Our partner desk will reach out within 48 hours.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="partners" data-testid="partner-portal-section" className="relative bg-[#062516] py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg:col-span-5"
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A880]">Global Partner Portal</span>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl text-[#F5F1E8] tracking-tight leading-tight">
              Bring the ritual <span className="italic text-[#C5A880]">to your market.</span>
            </h2>
            <p className="mt-6 text-[#F5F1E8]/65 text-base leading-relaxed font-light">
              We are onboarding a select group of international distributors, premium retailers
              and wellness houses. Request wholesale pricing and our full certification dossier.
            </p>

            <div className="mt-12 space-y-8">
              {PERKS.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                  className="flex gap-5"
                >
                  <div className="shrink-0 w-12 h-12 rounded-full border border-[#C5A880]/30 flex items-center justify-center">
                    <p.icon size={22} weight="duotone" className="text-[#C5A880]" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-[#F5F1E8]">{p.title}</h3>
                    <p className="mt-1 text-sm text-[#F5F1E8]/55 leading-relaxed font-light">{p.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
            className="lg:col-span-7"
          >
            <form onSubmit={submit} data-testid="partner-inquiry-form" className="glass-dark rounded-2xl p-8 lg:p-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#F5F1E8]/70 text-xs uppercase tracking-[0.15em]">Full Name *</Label>
                  <Input data-testid="partner-input-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Amara Osei" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#F5F1E8]/70 text-xs uppercase tracking-[0.15em]">Company *</Label>
                  <Input data-testid="partner-input-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Osei Fine Goods Ltd." className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#F5F1E8]/70 text-xs uppercase tracking-[0.15em]">Business Email *</Label>
                  <Input data-testid="partner-input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="amara@oseigoods.com" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#F5F1E8]/70 text-xs uppercase tracking-[0.15em]">Country / Region *</Label>
                  <Input data-testid="partner-input-country" value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="United Arab Emirates" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#F5F1E8]/70 text-xs uppercase tracking-[0.15em]">Distribution Channel *</Label>
                  <Select value={form.channel} onValueChange={(v) => set("channel", v)}>
                    <SelectTrigger data-testid="partner-select-channel" className={inputClass}>
                      <SelectValue placeholder="Select channel" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B3B24] border-[#C5A880]/30 text-[#F5F1E8]">
                      <SelectItem value="Wholesale / Distribution">Wholesale / Distribution</SelectItem>
                      <SelectItem value="Premium Retail">Premium Retail</SelectItem>
                      <SelectItem value="Duty Free / Travel">Duty Free / Travel</SelectItem>
                      <SelectItem value="Hospitality & Wellness">Hospitality & Wellness</SelectItem>
                      <SelectItem value="E-commerce">E-commerce</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#F5F1E8]/70 text-xs uppercase tracking-[0.15em]">Est. Monthly Volume *</Label>
                  <Select value={form.monthly_volume} onValueChange={(v) => set("monthly_volume", v)}>
                    <SelectTrigger data-testid="partner-select-volume" className={inputClass}>
                      <SelectValue placeholder="Select volume" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B3B24] border-[#C5A880]/30 text-[#F5F1E8]">
                      <SelectItem value="5,000 – 25,000 units">5,000 – 25,000 units</SelectItem>
                      <SelectItem value="25,000 – 100,000 units">25,000 – 100,000 units</SelectItem>
                      <SelectItem value="100,000 – 500,000 units">100,000 – 500,000 units</SelectItem>
                      <SelectItem value="500,000+ units">500,000+ units</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                <Label className="text-[#F5F1E8]/70 text-xs uppercase tracking-[0.15em]">Message</Label>
                <Textarea data-testid="partner-input-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your market, existing portfolio and launch timeline..." rows={4} className={`${inputClass} h-auto`} />
              </div>

              <div className="mt-6 flex items-start gap-3">
                <Checkbox
                  data-testid="partner-checkbox-certification"
                  id="certs"
                  checked={form.wants_certification}
                  onCheckedChange={(v) => set("wants_certification", Boolean(v))}
                  className="border-[#C5A880]/50 data-[state=checked]:bg-[#C5A880] data-[state=checked]:text-[#062516] mt-0.5"
                />
                <Label htmlFor="certs" className="text-sm text-[#F5F1E8]/70 font-light leading-relaxed cursor-pointer">
                  Send me the full certification dossier — GMP, organic certificates and lab reports.
                </Label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="partner-submit-button"
                className="mt-10 w-full rounded-full bg-[#C5A880] text-[#062516] py-4 text-sm uppercase tracking-[0.18em] font-medium hover:bg-[#D9BE93] disabled:opacity-60 transition-colors duration-300 flex items-center justify-center gap-3"
              >
                {submitting ? "Sending..." : "Request Wholesale Access"}
                <PaperPlaneTilt size={16} weight="bold" />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PartnerPortal;
